/**
 * Helpers for refreshing projects/records stores after successful workspace_action parts.
 */

import type { MessagePart, WorkspaceActionPayload } from "@/services/agent.service";

export type WorkspaceActionRefreshTargets = {
  projects: boolean;
  records: boolean;
};

type MessageWithParts = {
  parts?: MessagePart[] | null;
};

export type WorkspaceActionRefreshStores = {
  loadProjects: (force: boolean) => void | Promise<void>;
  invalidateRecords: () => void;
};

/**
 * Human-readable check: does a successful workspace action touch projects?
 */
export function isProjectWorkspaceAction(action: WorkspaceActionPayload): boolean {
  const entityType = action.entity_type?.toLowerCase() ?? "";
  const tool = action.tool.toLowerCase();
  return (
    entityType === "project" ||
    entityType === "project_report" ||
    tool.includes("project")
  );
}

/**
 * Human-readable check: does a successful workspace action touch records?
 */
export function isRecordWorkspaceAction(action: WorkspaceActionPayload): boolean {
  const entityType = action.entity_type?.toLowerCase() ?? "";
  const tool = action.tool.toLowerCase();
  return entityType === "record" || tool.includes("record");
}

/**
 * Scan UI/history messages for successful workspace_action parts and decide
 * which root stores should be refreshed.
 */
export function getWorkspaceActionRefreshTargets(
  messages: readonly MessageWithParts[]
): WorkspaceActionRefreshTargets {
  let projects = false;
  let records = false;

  for (const message of messages) {
    const parts = message.parts;
    if (!parts) {
      continue;
    }

    for (const part of parts) {
      if (part.type !== "workspace_action") {
        continue;
      }
      if (part.action.status !== "ok") {
        continue;
      }

      if (isProjectWorkspaceAction(part.action)) {
        projects = true;
      }
      if (isRecordWorkspaceAction(part.action)) {
        records = true;
      }

      if (projects && records) {
        return { projects, records };
      }
    }
  }

  return { projects, records };
}

/**
 * Refresh projects/records stores based on successful workspace_action parts.
 *
 * @returns The refresh targets that were applied (useful for tests).
 */
export function refreshStoresForWorkspaceActions(
  messages: readonly MessageWithParts[],
  stores: WorkspaceActionRefreshStores
): WorkspaceActionRefreshTargets {
  const targets = getWorkspaceActionRefreshTargets(messages);

  if (targets.projects) {
    void stores.loadProjects(true);
  }
  if (targets.records) {
    stores.invalidateRecords();
  }

  return targets;
}
